/**
 * Call History & Recording Viewer
 * Browse past calls, listen to recordings, view transcripts
 * Search and filter capabilities
 * 
 * MIGRATED TO REACT QUERY - Week 4 Phase 3
 * Uses: useCalls() hook with client-side filtering
 */

import React, { useState, useMemo } from 'react';
import { AdminPageLayout } from '@/components/layout/AdminPageLayout';
import { Card } from '@/components/ui/card';
import { useTheme } from '@/contexts/ThemeContext';
import { useCalls, useCallTranscript } from '@/lib/hooks';
import { Loader2 } from 'lucide-react';

interface Filters {
  dateFrom: string;
  dateTo: string;
  agent: string;
  status: string;
  minQuality: number;
  search: string;
}

export default function CallHistory() {
  const { theme } = useTheme();
  const [selectedCall, setSelectedCall] = useState<any>(null);
  const [filters, setFilters] = useState<Filters>({
    dateFrom: '',
    dateTo: '',
    agent: '',
    status: '',
    minQuality: 0,
    search: ''
  });

  // Fetch all calls using React Query hook (cached for 5 minutes)
  const { data: allCalls = [], isLoading } = useCalls();

  // Client-side filtering of cached data (no additional queries!)
  const filteredCalls = useMemo(() => {
    let filtered = [...allCalls];

    // Date from filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(call => 
        call.start_time && new Date(call.start_time) >= fromDate
      );
    }

    // Date to filter
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(call => 
        call.start_time && new Date(call.start_time) <= toDate
      );
    }

    // Agent filter
    if (filters.agent) {
      filtered = filtered.filter(call => call.agent_id === filters.agent);
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(call => call.status === filters.status);
    }

    // Quality score filter
    if (filters.minQuality > 0) {
      filtered = filtered.filter(call => 
        call.quality_score && call.quality_score >= filters.minQuality
      );
    }

    // Search filter (searches across multiple fields)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(call =>
        call.from_number?.toLowerCase().includes(searchLower) ||
        call.to_number?.toLowerCase().includes(searchLower) ||
        call.call_sid?.toLowerCase().includes(searchLower) ||
        call.summary?.toLowerCase().includes(searchLower)
      );
    }

    // Sort by most recent first
    return filtered.sort((a, b) => 
      new Date(b.start_time || 0).getTime() - new Date(a.start_time || 0).getTime()
    ).slice(0, 100); // Limit to 100 for performance
  }, [allCalls, filters]);

  // Fetch transcript using React Query hook (conditional loading)
  const { data: transcriptData = [] } = useCallTranscript(selectedCall?.id || '');

  const viewCall = (call: any) => {
    setSelectedCall(call);
  };

  const downloadRecording = (recordingUrl: string) => {
    window.open(recordingUrl, '_blank');
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <AdminPageLayout title="Call History" subtitle="Browse past calls, recordings, and transcripts">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: theme.colors.primaryColor }} />
            <div style={{ color: theme.colors.textSecondary }}>Loading call history...</div>
          </div>
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout title="Call History" subtitle="Browse past calls, recordings, and transcripts">
      <div className="flex gap-6" style={{ height: 'calc(100vh - 220px)' }}>
        {/* Left - Call List */}
        <div className="w-2/3">
          <Card className="h-full flex flex-col">
            {/* Filters */}
            <div 
              className="px-6 py-4"
              style={{
                backgroundColor: theme.colors.bgHover,
                borderBottom: `1px solid ${theme.colors.borderColor}`
              }}
            >
              <div className="grid grid-cols-4 gap-3">
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                  className="px-3 py-2 rounded"
                  style={{
                    backgroundColor: theme.colors.bgCard,
                    border: `1px solid ${theme.colors.borderColor}`,
                    color: theme.colors.textPrimary
                  }}
                  placeholder="From Date"
                />
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                  className="px-3 py-2 rounded"
                  style={{
                    backgroundColor: theme.colors.bgCard,
                    border: `1px solid ${theme.colors.borderColor}`,
                    color: theme.colors.textPrimary
                  }}
                  placeholder="To Date"
                />
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  className="px-3 py-2 rounded"
                  style={{
                    backgroundColor: theme.colors.bgCard,
                    border: `1px solid ${theme.colors.borderColor}`,
                    color: theme.colors.textPrimary
                  }}
                >
                  <option value="">All Statuses</option>
                  <option value="completed">Completed</option>
                  <option value="no_answer">No Answer</option>
                  <option value="canceled">Canceled</option>
                </select>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  placeholder="Search calls..."
                  className="px-3 py-2 rounded"
                  style={{
                    backgroundColor: theme.colors.bgCard,
                    border: `1px solid ${theme.colors.borderColor}`,
                    color: theme.colors.textPrimary
                  }}
                />
              </div>
            </div>

            {/* Call List */}
            <div className="flex-1 overflow-y-auto">
              {filteredCalls.map((call) => (
                <div
                  key={call.id}
                  onClick={() => viewCall(call)}
                  className="px-6 py-4 cursor-pointer"
                  style={{
                    borderBottom: `1px solid ${theme.colors.borderColor}`,
                    backgroundColor: selectedCall?.id === call.id ? theme.colors.bgHover : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCall?.id !== call.id) {
                      e.currentTarget.style.backgroundColor = theme.colors.bgHover;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCall?.id !== call.id) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium" style={{ color: theme.colors.textPrimary }}>
                          {call.from_number || 'Unknown'}
                        </span>
                        <span 
                          className="inline-flex px-2 py-1 text-xs font-medium rounded"
                          style={{
                            backgroundColor: call.direction === 'inbound' ? theme.colors.infoBg : 'rgba(168, 85, 247, 0.1)',
                            color: call.direction === 'inbound' ? theme.colors.infoText : '#9333ea'
                          }}
                        >
                          {call.direction}
                        </span>
                        {call.quality_score && (
                          <span 
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded"
                            style={{
                              backgroundColor: theme.colors.warningBg,
                              color: theme.colors.warningText
                            }}
                          >
                            ⭐ {call.quality_score.toFixed(1)}
                          </span>
                        )}
                      </div>
                      
                      <div className="text-sm" style={{ color: theme.colors.textSecondary }}>
                        {call.start_time && (
                          <>
                            <span>{new Date(call.start_time).toLocaleString()} • </span>
                            <span>Duration: {formatDuration(call.duration_seconds || 0)}</span>
                          </>
                        )}
                      </div>

                      {call.summary && (
                        <div className="text-sm mt-1 line-clamp-2" style={{ color: theme.colors.textTertiary }}>
                          {call.summary}
                        </div>
                      )}
                    </div>

                    {call.recording_url && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (call.recording_url) {
                            downloadRecording(call.recording_url);
                          }
                        }}
                        className="ml-4"
                        style={{ color: theme.colors.primaryColor }}
                        onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.primaryHoverColor}
                        onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.primaryColor}
                      >
                        🎧 Recording
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {filteredCalls.length === 0 && (
                <div className="flex items-center justify-center h-64" style={{ color: theme.colors.textTertiary }}>
                  No calls found matching your filters
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Right - Call Details */}
        <div className="w-1/3">
          <Card className="h-full overflow-y-auto">
            {selectedCall ? (
              <div>
                {/* Call Details Header */}
                <div 
                  className="px-6 py-4"
                  style={{
                    backgroundColor: theme.colors.bgHover,
                    borderBottom: `1px solid ${theme.colors.borderColor}`
                  }}
                >
                  <h2 className="font-bold" style={{ color: theme.colors.textPrimary }}>Call Details</h2>
                  <p className="text-sm" style={{ color: theme.colors.textSecondary }}>{selectedCall.call_sid}</p>
                </div>

                {/* Call Information */}
                <div className="px-6 py-4 space-y-4">
                  <div>
                    <label className="text-xs font-medium" style={{ color: theme.colors.textTertiary }}>Date & Time</label>
                    <p className="text-sm" style={{ color: theme.colors.textPrimary }}>
                      {selectedCall.start_time ? new Date(selectedCall.start_time).toLocaleString() : 'N/A'}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs font-medium" style={{ color: theme.colors.textTertiary }}>Duration</label>
                    <p className="text-sm" style={{ color: theme.colors.textPrimary }}>
                      {formatDuration(selectedCall.duration_seconds || 0)}
                    </p>
                  </div>

                  {selectedCall.sentiment_score !== null && selectedCall.sentiment_score !== undefined && (
                    <div>
                      <label className="text-xs font-medium" style={{ color: theme.colors.textTertiary }}>Sentiment</label>
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 rounded-full h-2" style={{ backgroundColor: theme.colors.bgHover }}>
                          <div 
                            className="h-2 rounded-full"
                            style={{ 
                              width: `${((selectedCall.sentiment_score + 1) / 2) * 100}%`,
                              backgroundColor: selectedCall.sentiment_score > 0.3 ? theme.colors.successBorder :
                                             selectedCall.sentiment_score < -0.3 ? theme.colors.dangerBorder : theme.colors.warningBorder
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium" style={{ color: theme.colors.textPrimary }}>
                          {selectedCall.sentiment_score > 0.3 ? '😊 Positive' :
                           selectedCall.sentiment_score < -0.3 ? '😞 Negative' : '😐 Neutral'}
                        </span>
                      </div>
                    </div>
                  )}

                  {selectedCall.summary && (
                    <div>
                      <label className="text-xs font-medium" style={{ color: theme.colors.textTertiary }}>AI Summary</label>
                      <p className="text-sm" style={{ color: theme.colors.textPrimary }}>{selectedCall.summary}</p>
                    </div>
                  )}

                  {/* Recording Player */}
                  {selectedCall.recording_url && (
                    <div>
                      <label className="text-xs font-medium mb-2 block" style={{ color: theme.colors.textTertiary }}>Recording</label>
                      <audio controls className="w-full">
                        <source src={selectedCall.recording_url} type="audio/mpeg" />
                      </audio>
                    </div>
                  )}

                  {/* Quality Score */}
                  {selectedCall.quality_score && (
                    <div>
                      <label className="text-xs font-medium" style={{ color: theme.colors.textTertiary }}>Quality Score</label>
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-bold" style={{ color: theme.colors.primaryColor }}>
                          {selectedCall.quality_score.toFixed(1)}
                        </span>
                        <span style={{ color: theme.colors.textSecondary }}>/ 5.0</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Transcript */}
                {transcriptData.length > 0 && (
                  <div className="px-6 py-4" style={{ borderTop: `1px solid ${theme.colors.borderColor}` }}>
                    <h3 className="font-semibold mb-3" style={{ color: theme.colors.textPrimary }}>Transcript</h3>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {transcriptData.map((segment) => (
                        <div 
                          key={segment.id} 
                          className="text-sm"
                          style={{
                            color: segment.speaker === 'agent' ? theme.colors.infoText : theme.colors.textPrimary
                          }}
                        >
                          <span className="font-medium">
                            {segment.speaker === 'agent' ? '🎧 Agent' : '👤 Customer'}:
                          </span>{' '}
                          {segment.text}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full" style={{ color: theme.colors.textTertiary }}>
                <div className="text-center">
                  <div className="text-5xl mb-3">📞</div>
                  <p>Select a call to view details</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </AdminPageLayout>
  );
}
